import {
  Modal,
  Box,
  IconButton,
  Typography,
  Fade,
  SxProps,
  Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { Stack } from "@mui/system";
import { ReactNode, useEffect } from "react";
import { useAppDispatch, useScrollWrapper } from "../../hooks";
import { setOpenModal } from "../../features/companySlice";

interface BaseModalProps {
  open: boolean;
  onClose: () => void;
  onBack?: () => void;
  onBackText?: string;
  title: string;
  icon?: ReactNode;
  children?: ReactNode;
  width?: { xs?: string | number; sm?: string | number; md: string | number };
  sx?: SxProps<Theme>;
  withMaxHeight?: boolean;
  btnBlock?: ReactNode;
  maxTitleWidth?: string;
  sxContentWrapper?: SxProps<Theme>;
}

export const BaseModal: React.FC<BaseModalProps> = ({
  open,
  onClose,
  onBack,
  onBackText,
  title,
  icon,
  children,
  width,
  sx,
  btnBlock,
  maxTitleWidth,
  sxContentWrapper,
}) => {
  const dispatch = useAppDispatch();
  const { scrollRef } = useScrollWrapper({ trigger: children });

  useEffect(() => {
    document.body.scrollHeight > window.innerHeight &&
      dispatch(setOpenModal(open));
    return () => {
      dispatch(setOpenModal(false));
    };
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      <Fade in={open} timeout={300} easing="exit">
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: width,
            border: "1px solid var(--gray, #D9D9D9)",
            bgcolor: "white",
            boxShadow: "0px 6px 18.1px 0px #0000000D",
            borderRadius: "8px",

            gap: 4,
            p: 4,
            ...sx,
          }}
        >
          <Box
            sx={{
              height: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {onBack && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton size="small" onClick={onBack}>
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <Typography variant="body1">{onBackText}</Typography>
              </Box>
            )}
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ ml: "auto", color: "#828282" }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {icon && <Stack sx={{ justifyContent: "center" }}>{icon}</Stack>}
            {title && (
              <Typography
                variant="h4"
                component="h4"
                sx={{
                  textAlign: "left",
                  color: "custom.textDarkBlue",
                  fontWeight: 500,
                }}
                maxWidth={maxTitleWidth}
              >
                {title}
              </Typography>
            )}
          </Box>

          <Box
            ref={scrollRef}
            sx={{
              mt: 1,
              pr: 0,
              maxHeight: "calc(100vh - 210px)",
              overflowY: "auto",
              "&::-webkit-scrollbar": {
                width: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#aaa",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-track": {
                width: "12px",
                backgroundColor: "transparent",
              },
              ...sxContentWrapper,
            }}
          >
            {children}
          </Box>
          {btnBlock}
        </Box>
      </Fade>
    </Modal>
  );
};
